/**
 * EnterprisePaymentsAdmin
 * Super Admin — Finance > Enterprise Payments
 *
 * Features:
 *  - Summary cards (Pending, Submitted, Approved, Rejected)
 *  - Filterable paginated table
 *  - Detail drawer with audit timeline
 *  - Approve / Reject / Request Clarification modals
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import {
  Building2, Check, X, AlertCircle, RefreshCw, Search, Filter,
  ChevronDown, ChevronRight, Download, Eye, FileText, Clock,
  Banknote, Shield, CheckCircle, XCircle, HelpCircle, Calendar,
  ChevronLeft,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────
const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const formatDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_CONFIG = {
  PENDING:                 { label: "Pending",                bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" },
  SUBMITTED:               { label: "Submitted",              bg: "#EFF6FF", text: "#1E40AF", dot: "#3B82F6" },
  RESUBMITTED:             { label: "Resubmitted",            bg: "#F0F9FF", text: "#0369A1", dot: "#0EA5E9" },
  UNDER_VERIFICATION:      { label: "Under Verification",     bg: "#FFFBEB", text: "#92400E", dot: "#F59E0B" },
  APPROVED:                { label: "Approved",               bg: "#F0FDF4", text: "#065F46", dot: "#10B981" },
  REJECTED:                { label: "Rejected",               bg: "#FEF2F2", text: "#7F1D1D", dot: "#EF4444" },
  CLARIFICATION_REQUESTED: { label: "Clarification Requested",bg: "#FFF7ED", text: "#7C2D12", dot: "#EA580C" },
  CONVERTED:               { label: "Converted",              bg: "#F0FDF4", text: "#14532D", dot: "#059669" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "4px 10px", borderRadius: "99px",
      background: cfg.bg, color: cfg.text,
      fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap",
    }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// ── Summary Card ──────────────────────────────────────────────
const SummaryCard = ({ label, value, icon: Icon, color, bg, border }) => (
  <div style={{
    background: bg || "#FFFFFF", border: `1.5px solid ${border || "#E2E8F0"}`,
    borderRadius: "16px", padding: "18px 20px",
    display: "flex", alignItems: "center", gap: "14px",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
  }}>
    <div style={{
      width: "44px", height: "44px", borderRadius: "12px",
      background: `rgba(${color},0.10)`,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon style={{ width: "20px", height: "20px", color: `rgb(${color})` }} />
    </div>
    <div>
      <p style={{ fontSize: "24px", fontWeight: 900, color: "#0F172A", margin: 0, letterSpacing: "-0.02em" }}>{value}</p>
      <p style={{ fontSize: "12px", color: "#64748B", margin: 0, fontWeight: 500 }}>{label}</p>
    </div>
  </div>
);

// ── Modal ─────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
    }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "480px",
        boxShadow: "0 24px 64px rgba(15,23,42,0.18)",
        overflow: "hidden",
      }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex" }}>
            <X style={{ width: "18px", height: "18px" }} />
          </button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
};

// ── Detail Drawer ─────────────────────────────────────────────
const DetailDrawer = ({ purchase, audit, onClose, onApprove, onReject, onClarify, loading }) => {
  if (!purchase) return null;

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: "480px", maxWidth: "100vw",
      background: "#fff", boxShadow: "-8px 0 32px rgba(15,23,42,0.12)",
      zIndex: 900, display: "flex", flexDirection: "column",
      overflowY: "auto",
    }}>
      {/* Drawer header */}
      <div style={{
        padding: "20px 24px", borderBottom: "1px solid #F1F5F9",
        display: "flex", alignItems: "center", gap: "12px",
        position: "sticky", top: 0, background: "#fff", zIndex: 1,
      }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex" }}>
          <ChevronRight style={{ width: "20px", height: "20px" }} />
        </button>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: "14px", fontWeight: 800, color: "#0F172A", margin: 0 }}>
            {purchase.quotation_number}
          </p>
          <StatusBadge status={purchase.status} />
        </div>
      </div>

      <div style={{ padding: "24px", flex: 1 }}>
        {/* College Info */}
        <section style={{ marginBottom: "20px" }}>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>College Information</p>
          {[
            { label: "College",  value: purchase.college_name },
            { label: "Email",    value: purchase.college_email },
            { label: "Plan",     value: purchase.plan_name || purchase.plan_code },
            { label: "Credits",  value: (purchase.credits_purchased || 0).toLocaleString("en-IN") },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #F8FAFC" }}>
              <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>{label}</span>
              <span style={{ fontSize: "13px", color: "#0F172A", fontWeight: 600 }}>{value || "—"}</span>
            </div>
          ))}
        </section>

        {/* Payment Amounts */}
        <section style={{ marginBottom: "20px", background: "#F8FAFC", borderRadius: "12px", padding: "14px 16px" }}>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Payment Summary</p>
          {[
            { label: "Subtotal",   value: formatINR(purchase.subtotal) },
            { label: "GST @ 18%", value: formatINR(purchase.gst) },
            { label: "Grand Total",value: formatINR(purchase.grand_total), bold: true, accent: "#4F46E5" },
          ].map(({ label, value, bold, accent }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: bold ? "2px solid #E2E8F0" : "1px dashed #E2E8F0" }}>
              <span style={{ fontSize: "12px", color: "#64748B", fontWeight: bold ? 700 : 500 }}>{label}</span>
              <span style={{ fontSize: bold ? "16px" : "13px", fontWeight: bold ? 800 : 600, color: accent || "#0F172A" }}>{value}</span>
            </div>
          ))}
        </section>

        {/* Payment Details */}
        <section style={{ marginBottom: "20px" }}>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Submitted Payment Details</p>

          <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #F8FAFC" }}>
            <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>UTR / Reference</span>
            <span style={{ fontSize: "13px", color: "#0F172A", fontWeight: 700, fontFamily: "monospace" }}>
              {purchase.utr_number || <span style={{ color: "#94A3B8", fontWeight: 400, fontFamily: "inherit" }}>Not submitted</span>}
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #F8FAFC" }}>
            <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>Submitted At</span>
            <span style={{ fontSize: "12px", color: "#0F172A", fontWeight: 500 }}>{formatDate(purchase.submitted_at)}</span>
          </div>

          {purchase.remarks && (
            <div style={{ marginTop: "10px", padding: "10px 12px", background: "#F8FAFC", borderRadius: "8px" }}>
              <p style={{ fontSize: "11px", color: "#94A3B8", fontWeight: 600, margin: "0 0 4px" }}>REMARKS</p>
              <p style={{ fontSize: "12px", color: "#475569", margin: 0, lineHeight: 1.5 }}>{purchase.remarks}</p>
            </div>
          )}
        </section>

        {/* File Attachments */}
        {(purchase.purchase_order_path || purchase.payment_proof_path) && (
          <section style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Attachments</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {purchase.purchase_order_path && (
                <a href={purchase.purchase_order_path} target="_blank" rel="noreferrer" style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "10px 14px", borderRadius: "10px",
                  background: "#F8FAFC", border: "1px solid #E2E8F0",
                  textDecoration: "none", color: "#1E40AF",
                }}>
                  <FileText style={{ width: "14px", height: "14px" }} />
                  <span style={{ fontSize: "12px", fontWeight: 600, flex: 1 }}>Purchase Order</span>
                  <Download style={{ width: "13px", height: "13px" }} />
                </a>
              )}
              {purchase.payment_proof_path && (
                <a href={purchase.payment_proof_path} target="_blank" rel="noreferrer" style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "10px 14px", borderRadius: "10px",
                  background: "#F8FAFC", border: "1px solid #E2E8F0",
                  textDecoration: "none", color: "#1E40AF",
                }}>
                  <FileText style={{ width: "14px", height: "14px" }} />
                  <span style={{ fontSize: "12px", fontWeight: 600, flex: 1 }}>Payment Proof</span>
                  <Download style={{ width: "13px", height: "13px" }} />
                </a>
              )}
            </div>
          </section>
        )}

        {/* Invoice Info (if approved) */}
        {purchase.invoice_number && (
          <section style={{
            marginBottom: "20px", background: "#F0FDF4", border: "1px solid #BBF7D0",
            borderRadius: "12px", padding: "14px 16px",
          }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#065F46", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px" }}>Invoice Generated</p>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "12px", color: "#15803D" }}>Invoice Number</span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#14532D" }}>{purchase.invoice_number}</span>
            </div>
            {purchase.verified_by && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#15803D" }}>Approved By</span>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#14532D" }}>{purchase.verified_by}</span>
              </div>
            )}
            {purchase.activated_at && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#15803D" }}>Activated At</span>
                <span style={{ fontSize: "12px", color: "#14532D" }}>{formatDate(purchase.activated_at)}</span>
              </div>
            )}
          </section>
        )}

        {/* Rejection reason (if rejected) */}
        {purchase.rejection_reason && (
          <section style={{
            marginBottom: "20px", background: "#FEF2F2", border: "1px solid #FECACA",
            borderRadius: "12px", padding: "14px 16px",
          }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#7F1D1D", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>Rejection Reason</p>
            <p style={{ fontSize: "13px", color: "#991B1B", margin: 0, lineHeight: 1.5 }}>{purchase.rejection_reason}</p>
          </section>
        )}

        {/* College Responded banner — shown when status is RESUBMITTED */}
        {purchase.status === "RESUBMITTED" && (
          <section style={{ marginBottom: "20px", borderRadius: "12px", overflow: "hidden", border: "2px solid #BAE6FD" }}>
            <div style={{ background: "linear-gradient(135deg,#0369A1,#0EA5E9)", padding: "10px 14px", display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircle style={{ width: "14px", height: "14px", color: "#fff", flexShrink: 0 }} />
              <span style={{ fontSize: "12px", fontWeight: 800, color: "#fff" }}>College Responded to Clarification</span>
            </div>
            <div style={{ background: "#F0F9FF", padding: "12px 14px" }}>
              <p style={{ fontSize: "12px", color: "#0369A1", margin: 0, lineHeight: 1.5 }}>
                The college has updated their payment information and resubmitted for review. Check the updated UTR, attachments, and remarks above before approving or rejecting.
              </p>
            </div>
          </section>
        )}

        {/* Clarification message */}
        {purchase.clarification_message && (
          <section style={{
            marginBottom: "20px", background: "#FFFBEB", border: "1px solid #FDE68A",
            borderRadius: "12px", padding: "14px 16px",
          }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>Clarification Previously Requested</p>
            <p style={{ fontSize: "13px", color: "#B45309", margin: 0, lineHeight: 1.5 }}>{purchase.clarification_message}</p>
          </section>
        )}

        {/* Audit Timeline */}
        {audit && audit.length > 0 && (
          <section style={{ marginBottom: "24px" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 12px" }}>Audit Timeline</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {audit.map((entry, i) => (
                <div key={entry.id || i} style={{ display: "flex", gap: "10px", paddingBottom: "12px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <div style={{
                      width: "26px", height: "26px", borderRadius: "50%",
                      background: entry.action.includes("APPROVED") ? "#D1FAE5"
                        : entry.action.includes("REJECTED") ? "#FEE2E2"
                        : "#EFF6FF",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px solid #fff",
                      boxShadow: "0 0 0 1px #E2E8F0",
                    }}>
                      {entry.action.includes("APPROVED")     ? <CheckCircle style={{ width: "12px", height: "12px", color: "#059669" }} />
                       : entry.action.includes("REJECTED")   ? <XCircle     style={{ width: "12px", height: "12px", color: "#DC2626" }} />
                       : entry.action.includes("CLARIF")     ? <HelpCircle  style={{ width: "12px", height: "12px", color: "#D97706" }} />
                       : <Clock style={{ width: "12px", height: "12px", color: "#4F46E5" }} />}
                    </div>
                    {i < audit.length - 1 && (
                      <div style={{ width: "1px", flex: 1, background: "#E2E8F0", margin: "3px 0" }} />
                    )}
                  </div>
                  <div style={{ flex: 1, paddingTop: "3px" }}>
                    <p style={{ fontSize: "12px", fontWeight: 700, color: "#0F172A", margin: "0 0 2px" }}>
                      {entry.action.replace(/_/g, " ")}
                    </p>
                    <p style={{ fontSize: "11px", color: "#64748B", margin: "0 0 2px", lineHeight: 1.4 }}>
                      {entry.description}
                    </p>
                    <p style={{ fontSize: "10px", color: "#94A3B8", margin: 0 }}>
                      {entry.performed_by} · {formatDate(entry.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Action buttons — only for actionable statuses */}
        {["SUBMITTED", "RESUBMITTED", "UNDER_VERIFICATION"].includes(purchase.status) && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <button
              onClick={() => onApprove(purchase)}
              disabled={loading}
              style={{
                padding: "13px", borderRadius: "12px", border: "none",
                background: "linear-gradient(135deg,#059669,#065F46)", color: "#fff",
                fontSize: "14px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                opacity: loading ? 0.7 : 1,
              }}
            >
              <CheckCircle style={{ width: "16px", height: "16px" }} />
              Approve & Activate Credits
            </button>
            <button
              onClick={() => onClarify(purchase)}
              disabled={loading}
              style={{
                padding: "13px", borderRadius: "12px",
                border: "1.5px solid #FDE68A", background: "#FFFBEB",
                fontSize: "14px", fontWeight: 700, color: "#B45309", cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
            >
              <HelpCircle style={{ width: "16px", height: "16px" }} />
              Request Clarification
            </button>
            <button
              onClick={() => onReject(purchase)}
              disabled={loading}
              style={{
                padding: "13px", borderRadius: "12px",
                border: "1.5px solid #FECACA", background: "#FEF2F2",
                fontSize: "14px", fontWeight: 700, color: "#DC2626", cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
            >
              <XCircle style={{ width: "16px", height: "16px" }} />
              Reject Payment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// EnterprisePaymentsAdmin
// ════════════════════════════════════════════════════════════════
const EnterprisePaymentsAdmin = () => {
  const [purchases,  setPurchases]  = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [search,       setSearch]       = useState("");
  const [fromDate,     setFromDate]     = useState("");
  const [toDate,       setToDate]       = useState("");

  // Drawer
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [drawerAudit,      setDrawerAudit]      = useState([]);
  const [drawerLoading,    setDrawerLoading]    = useState(false);

  // Modals
  const [approveModal,     setApproveModal]     = useState(null);
  const [rejectModal,      setRejectModal]      = useState(null);
  const [clarifyModal,     setClarifyModal]     = useState(null);

  const [adminNotes,       setAdminNotes]       = useState("");
  const [rejectReason,     setRejectReason]     = useState("");
  const [clarifyMessage,   setClarifyMessage]   = useState("");
  const [actionLoading,    setActionLoading]    = useState(false);

  const currentPage = pagination.page;

  const fetchPurchases = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (statusFilter) params.set("status", statusFilter);
      if (search)       params.set("search", search);
      if (fromDate)     params.set("fromDate", fromDate);
      if (toDate)       params.set("toDate", toDate);

      const res = await axios.get(`${API_BASE_URL}/credits/enterprise/admin/purchases?${params}`);
      if (res.data.success) {
        setPurchases(res.data.purchases);
        setPagination(res.data.pagination);
        setStats(res.data.stats);
      } else {
        setError(res.data.error || "Failed to load enterprise purchases.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load enterprise purchases.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, fromDate, toDate]);

  useEffect(() => { fetchPurchases(1); }, [fetchPurchases]);

  const openDrawer = async (purchase) => {
    setSelectedPurchase(purchase);
    setDrawerLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/credits/enterprise/admin/purchases/${purchase.id}?admin=true`
      );
      if (res.data.success) {
        setSelectedPurchase(res.data.purchase);
        setDrawerAudit(res.data.audit || []);
      }
    } catch (err) {
      console.error("[openDrawer] Error:", err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setActionLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await axios.post(`${API_BASE_URL}/credits/enterprise/admin/approve`, {
        enterprise_purchase_id: approveModal.id,
        verified_by:  user?.name || user?.email || "Super Admin",
        admin_notes:  adminNotes.trim() || undefined,
      });
      if (res.data.success) {
        setApproveModal(null);
        setAdminNotes("");
        setSelectedPurchase(null);
        fetchPurchases(currentPage);
      } else {
        alert(res.data.error || "Failed to approve.");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to approve enterprise purchase.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) { alert("Please enter a rejection reason."); return; }
    setActionLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await axios.post(`${API_BASE_URL}/credits/enterprise/admin/reject`, {
        enterprise_purchase_id: rejectModal.id,
        rejected_by:      user?.name || user?.email || "Super Admin",
        rejection_reason: rejectReason.trim(),
      });
      if (res.data.success) {
        setRejectModal(null);
        setRejectReason("");
        setSelectedPurchase(null);
        fetchPurchases(currentPage);
      } else {
        alert(res.data.error || "Failed to reject.");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to reject enterprise purchase.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleClarify = async () => {
    if (!clarifyModal || !clarifyMessage.trim()) { alert("Please enter a clarification message."); return; }
    setActionLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await axios.post(`${API_BASE_URL}/credits/enterprise/admin/clarify`, {
        enterprise_purchase_id: clarifyModal.id,
        clarified_by:         user?.name || user?.email || "Super Admin",
        clarification_message: clarifyMessage.trim(),
      });
      if (res.data.success) {
        setClarifyModal(null);
        setClarifyMessage("");
        setSelectedPurchase(null);
        fetchPurchases(currentPage);
      } else {
        alert(res.data.error || "Failed to send clarification.");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to request clarification.");
    } finally {
      setActionLoading(false);
    }
  };

  const FILTER_STATUSES = [
    { value: "",                       label: "All Statuses" },
    { value: "PENDING",                label: "Pending" },
    { value: "SUBMITTED",              label: "Submitted" },
    { value: "RESUBMITTED",            label: "Resubmitted" },
    { value: "UNDER_VERIFICATION",     label: "Under Verification" },
    { value: "CLARIFICATION_REQUESTED",label: "Clarification Requested" },
    { value: "APPROVED",               label: "Approved" },
    { value: "REJECTED",               label: "Rejected" },
  ];

  return (
    <div style={{ padding: "32px", background: "#F8FAFC", minHeight: "100vh", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: "28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "rgba(124,58,237,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Building2 style={{ width: "18px", height: "18px", color: "#7C3AED" }} />
          </div>
          <h1 style={{ fontSize: "24px", fontWeight: 900, color: "#0F172A", margin: 0, letterSpacing: "-0.02em" }}>
            Enterprise Payments
          </h1>
        </div>
        <p style={{ fontSize: "13px", color: "#64748B", margin: "0 0 0 48px", fontWeight: 500 }}>
          Review, verify, and approve institutional bank transfer payments
        </p>
      </div>

      {/* ── Summary Cards ── */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "14px", marginBottom: "24px" }}>
          <SummaryCard label="Total Purchases"   value={stats.total || 0}    icon={Building2}    color="79,70,229" />
          <SummaryCard label="Pending Review"    value={(parseInt(stats.submitted || 0) + parseInt(stats.resubmitted || 0) + parseInt(stats.under_verification || 0))} icon={Clock} color="245,158,11" bg="#FFFBEB" border="#FDE68A" />
          <SummaryCard label="Approved"          value={stats.approved || 0} icon={CheckCircle}  color="5,150,105"  bg="#F0FDF4" border="#BBF7D0" />
          <SummaryCard label="Rejected"          value={stats.rejected || 0} icon={XCircle}      color="220,38,38"  bg="#FEF2F2" border="#FECACA" />
          <SummaryCard label="Approved Amount"   value={"₹" + (parseFloat(stats.total_approved_amount || 0) / 100000).toFixed(1) + "L"} icon={Banknote} color="5,150,105" bg="#F0FDF4" border="#BBF7D0" />
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{
        background: "#FFFFFF", border: "1.5px solid #E2E8F0",
        borderRadius: "16px", padding: "16px 20px",
        marginBottom: "20px",
        display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center",
      }}>
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 200px", minWidth: "200px" }}>
          <Search style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", width: "14px", height: "14px", color: "#94A3B8" }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search college, quotation, UTR..."
            style={{
              width: "100%", padding: "9px 12px 9px 32px",
              borderRadius: "10px", border: "1.5px solid #E2E8F0",
              background: "#F8FAFC", fontSize: "13px", color: "#0F172A",
              outline: "none", boxSizing: "border-box",
            }}
            onKeyDown={(e) => { if (e.key === "Enter") fetchPurchases(1); }}
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "9px 12px", borderRadius: "10px",
            border: "1.5px solid #E2E8F0", background: "#F8FAFC",
            fontSize: "13px", color: "#0F172A", cursor: "pointer", outline: "none",
          }}
        >
          {FILTER_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Date filters */}
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", color: "#475569", outline: "none" }} />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", color: "#475569", outline: "none" }} />

        <button
          onClick={() => fetchPurchases(1)}
          style={{
            padding: "9px 18px", borderRadius: "10px", border: "none",
            background: "linear-gradient(135deg,#7C3AED,#4C1D95)", color: "#fff",
            fontSize: "13px", fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <Filter style={{ width: "13px", height: "13px" }} />
          Filter
        </button>

        {(search || statusFilter || fromDate || toDate) && (
          <button
            onClick={() => { setSearch(""); setStatusFilter(""); setFromDate(""); setToDate(""); setTimeout(() => fetchPurchases(1), 0); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: "#94A3B8", fontWeight: 500 }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{
        background: "#FFFFFF", border: "1.5px solid #E2E8F0",
        borderRadius: "20px", overflow: "hidden",
        boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
      }}>
        {loading ? (
          <div style={{ padding: "60px", textAlign: "center" }}>
            <RefreshCw style={{ width: "28px", height: "28px", color: "#7C3AED", margin: "0 auto 12px", animation: "spin 1s linear infinite", display: "block" }} />
            <p style={{ fontSize: "13px", color: "#64748B", fontWeight: 500 }}>Loading enterprise purchases...</p>
          </div>
        ) : error ? (
          <div style={{ padding: "60px", textAlign: "center" }}>
            <AlertCircle style={{ width: "28px", height: "28px", color: "#DC2626", margin: "0 auto 12px", display: "block" }} />
            <p style={{ fontSize: "13px", color: "#DC2626", fontWeight: 500 }}>{error}</p>
            <button onClick={() => fetchPurchases(1)} style={{ marginTop: "12px", padding: "8px 20px", borderRadius: "10px", background: "#7C3AED", color: "#fff", border: "none", fontWeight: 600, cursor: "pointer" }}>
              Retry
            </button>
          </div>
        ) : purchases.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center" }}>
            <Building2 style={{ width: "36px", height: "36px", color: "#CBD5E1", margin: "0 auto 16px", display: "block" }} />
            <p style={{ fontSize: "15px", fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>No enterprise purchases found</p>
            <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>Enterprise purchases will appear here once colleges generate quotations for amounts above ₹5,00,000.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                    {["Quotation #", "College", "Plan", "Credits", "Grand Total", "UTR", "Status", "Date", "Actions"].map((h) => (
                      <th key={h} style={{ padding: "12px 16px", fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p, i) => (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: "1px solid #F8FAFC",
                        borderLeft: p.status === "RESUBMITTED" ? "3px solid #0EA5E9" : "3px solid transparent",
                        background: selectedPurchase?.id === p.id ? "#F5F3FF" : p.status === "RESUBMITTED" ? "#F0F9FF" : i % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
                        cursor: "pointer",
                        transition: "background 100ms",
                      }}
                      onMouseEnter={(e) => { if (selectedPurchase?.id !== p.id) e.currentTarget.style.background = "#F8FAFC"; }}
                      onMouseLeave={(e) => { if (selectedPurchase?.id !== p.id) e.currentTarget.style.background = p.status === "RESUBMITTED" ? "#F0F9FF" : i % 2 === 0 ? "#FFFFFF" : "#FAFAFA"; }}
                      onClick={() => openDrawer(p)}
                    >
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#7C3AED", fontFamily: "monospace" }}>
                          {p.quotation_number}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{p.college_name}</span>
                        <br />
                        <span style={{ fontSize: "11px", color: "#94A3B8" }}>{p.college_email}</span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>{p.plan_name || p.plan_code || "—"}</span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A" }}>
                          {(p.credits_purchased || 0).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 800, color: "#4F46E5" }}>
                          {formatINR(p.grand_total)}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        {p.utr_number
                          ? <span style={{ fontSize: "12px", fontFamily: "monospace", fontWeight: 600, color: "#0F172A", background: "#F1F5F9", padding: "3px 8px", borderRadius: "6px" }}>{p.utr_number}</span>
                          : <span style={{ fontSize: "11px", color: "#CBD5E1" }}>Not submitted</span>
                        }
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <StatusBadge status={p.status} />
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: "11px", color: "#64748B" }}>
                          {new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); openDrawer(p); }}
                          style={{
                            display: "flex", alignItems: "center", gap: "4px",
                            padding: "6px 12px", borderRadius: "8px",
                            border: "1.5px solid #E2E8F0", background: "#F8FAFC",
                            fontSize: "12px", fontWeight: 600, color: "#475569",
                            cursor: "pointer",
                          }}
                        >
                          <Eye style={{ width: "12px", height: "12px" }} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                padding: "14px 20px", borderTop: "1px solid #F1F5F9",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>
                  Showing {purchases.length} of {pagination.total} purchases
                </span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => fetchPurchases(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "7px 14px", borderRadius: "8px",
                      border: "1.5px solid #E2E8F0", background: currentPage === 1 ? "#F8FAFC" : "#fff",
                      fontSize: "13px", fontWeight: 600, color: currentPage === 1 ? "#CBD5E1" : "#475569",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "4px",
                    }}
                  >
                    <ChevronLeft style={{ width: "14px", height: "14px" }} /> Prev
                  </button>
                  <span style={{ padding: "7px 14px", fontSize: "13px", fontWeight: 700, color: "#0F172A" }}>
                    {currentPage} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchPurchases(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    style={{
                      padding: "7px 14px", borderRadius: "8px",
                      border: "1.5px solid #E2E8F0", background: currentPage === pagination.totalPages ? "#F8FAFC" : "#fff",
                      fontSize: "13px", fontWeight: 600, color: currentPage === pagination.totalPages ? "#CBD5E1" : "#475569",
                      cursor: currentPage === pagination.totalPages ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "4px",
                    }}
                  >
                    Next <ChevronRight style={{ width: "14px", height: "14px" }} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Detail Drawer ── */}
      {selectedPurchase && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.2)", zIndex: 800 }}
            onClick={() => setSelectedPurchase(null)}
          />
          <DetailDrawer
            purchase={selectedPurchase}
            audit={drawerAudit}
            loading={drawerLoading}
            onClose={() => setSelectedPurchase(null)}
            onApprove={(p) => setApproveModal(p)}
            onReject={(p)  => setRejectModal(p)}
            onClarify={(p) => setClarifyModal(p)}
          />
        </>
      )}

      {/* ── APPROVE Modal ── */}
      <Modal isOpen={!!approveModal} onClose={() => setApproveModal(null)} title="Approve Enterprise Payment">
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "12px", padding: "14px 16px" }}>
            <p style={{ fontSize: "13px", fontWeight: 700, color: "#14532D", margin: "0 0 4px" }}>Confirm Approval</p>
            <p style={{ fontSize: "12px", color: "#15803D", margin: 0, lineHeight: 1.5 }}>
              Approving will instantly activate <strong>{(approveModal?.credits_purchased || 0).toLocaleString("en-IN")} credits</strong> for <strong>{approveModal?.college_name}</strong>.
              This action is <strong>irreversible</strong>. A GST invoice will be generated automatically.
            </p>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
              Admin Notes (Optional)
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="e.g. UTR verified against bank statement dated 18-Jun-2026..."
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => setApproveModal(null)} style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={actionLoading}
              style={{
                flex: 2, padding: "12px", borderRadius: "10px", border: "none",
                background: actionLoading ? "#94A3B8" : "linear-gradient(135deg,#059669,#065F46)",
                color: "#fff", fontSize: "13px", fontWeight: 700,
                cursor: actionLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              }}
            >
              {actionLoading ? <><RefreshCw style={{ width: "13px", height: "13px", animation: "spin 1s linear infinite" }} /> Approving...</> : <><CheckCircle style={{ width: "14px", height: "14px" }} /> Approve & Activate</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── REJECT Modal ── */}
      <Modal isOpen={!!rejectModal} onClose={() => setRejectModal(null)} title="Reject Enterprise Payment">
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "12px", padding: "12px 14px" }}>
            <p style={{ fontSize: "12px", color: "#991B1B", margin: 0, lineHeight: 1.5 }}>
              Rejecting will notify the college via email. Credits will NOT be activated.
            </p>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
              Rejection Reason <span style={{ color: "#DC2626" }}>*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. UTR not found in bank records. Amount mismatch. Please verify and resubmit."
              rows={4}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => setRejectModal(null)} style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={actionLoading}
              style={{
                flex: 2, padding: "12px", borderRadius: "10px", border: "none",
                background: actionLoading ? "#94A3B8" : "#DC2626",
                color: "#fff", fontSize: "13px", fontWeight: 700,
                cursor: actionLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              }}
            >
              {actionLoading ? <><RefreshCw style={{ width: "13px", height: "13px", animation: "spin 1s linear infinite" }} /> Rejecting...</> : <><XCircle style={{ width: "14px", height: "14px" }} /> Reject Payment</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── CLARIFY Modal ── */}
      <Modal isOpen={!!clarifyModal} onClose={() => setClarifyModal(null)} title="Request Clarification">
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "12px", padding: "12px 14px" }}>
            <p style={{ fontSize: "12px", color: "#B45309", margin: 0, lineHeight: 1.5 }}>
              The college will be notified and can update their UTR, payment proof, and remarks before resubmitting.
            </p>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
              Clarification Message <span style={{ color: "#DC2626" }}>*</span>
            </label>
            <textarea
              value={clarifyMessage}
              onChange={(e) => setClarifyMessage(e.target.value)}
              placeholder="e.g. Please upload a clear screenshot of the bank transfer confirmation showing the UTR number and amount."
              rows={4}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => setClarifyModal(null)} style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              Cancel
            </button>
            <button
              onClick={handleClarify}
              disabled={actionLoading}
              style={{
                flex: 2, padding: "12px", borderRadius: "10px", border: "none",
                background: actionLoading ? "#94A3B8" : "linear-gradient(135deg,#D97706,#92400E)",
                color: "#fff", fontSize: "13px", fontWeight: 700,
                cursor: actionLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              }}
            >
              {actionLoading ? <><RefreshCw style={{ width: "13px", height: "13px", animation: "spin 1s linear infinite" }} /> Sending...</> : <><HelpCircle style={{ width: "14px", height: "14px" }} /> Send Request</>}
            </button>
          </div>
        </div>
      </Modal>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default EnterprisePaymentsAdmin;
